/**
 * Domain Permission Registry — delegation & sensitivity SSOT (Stream C)
 *
 * Consumers:
 * - internal-entity-permission-matrix.ts (DELEGATABLE_SUBDOMAINS, labels, catalog)
 * - SensitiveActionRevalidationService (Stream B / CR3 — isSensitivePermission)
 * - Stream D UI (matrix grouping — not implemented here)
 */

import type { BusinessDomain } from '@shared/types/business-domain';
import { getBusinessDomainDisplayName } from '@shared/types/business-domain';

export type RiskLevel = 'low' | 'medium' | 'high';

export interface DomainPermissionEntry {
  /** Business subdomain key (e.g. finance_state) */
  key: BusinessDomain;
  /** Operator-facing label (matrix UI) */
  label: string;
  /** Domain category for grouping */
  category: string;
  /** Shown in KD-3 MVP delegation matrix */
  visibleInMvp: boolean;
  /** Approve actions require fresh DB permission revalidation (Stream B) */
  sensitive: boolean;
  /** Approve actions require MFA step-up before grant */
  requiresMfaForApprove: boolean;
  /** `{subdomain}:approve` triggers DB-fresh permission check (CR3) */
  requiresDbRevalidation: boolean;
  /** Operator-facing risk tier for approve / delegation (CR3) */
  riskLevel: RiskLevel;
}

const MVP_DELEGATABLE_KEYS = [
  'internal_entity',
  'external_entity',
  'sellable_item',
  'service_item',
  'asset_item',
  'raw_material_item',
  'finance_state',
  'finance_flow',
  'finance_budget',
  'physical_space',
  'virtual_space',
  'stream_execution',
  'calendar_trigger',
  'manufacturing_bom',
  'production_order',
  'quality_inspection',
  'legal_entity',
  'currency_rate',
  'intercompany',
  'consumable_item',
  'knowledge_report',
] as const satisfies readonly BusinessDomain[];

export type MvpDelegatableSubdomain = (typeof MVP_DELEGATABLE_KEYS)[number];

function defaultRiskLevel(
  sensitive: boolean,
  requiresMfaForApprove: boolean,
  explicit?: RiskLevel
): RiskLevel {
  if (explicit) return explicit;
  if (sensitive && requiresMfaForApprove) return 'high';
  if (sensitive) return 'medium';
  return 'low';
}

function entry(
  key: BusinessDomain,
  overrides: Partial<
    Pick<
      DomainPermissionEntry,
      | 'label'
      | 'category'
      | 'visibleInMvp'
      | 'sensitive'
      | 'requiresMfaForApprove'
      | 'requiresDbRevalidation'
      | 'riskLevel'
    >
  > = {}
): DomainPermissionEntry {
  const category = overrides.category ?? inferCategory(key);
  const sensitive = overrides.sensitive ?? false;
  const requiresMfaForApprove = overrides.requiresMfaForApprove ?? false;
  return {
    key,
    label: overrides.label ?? labelFor(key),
    category,
    visibleInMvp: overrides.visibleInMvp ?? false,
    sensitive,
    requiresMfaForApprove,
    requiresDbRevalidation: overrides.requiresDbRevalidation ?? sensitive,
    riskLevel: defaultRiskLevel(sensitive, requiresMfaForApprove, overrides.riskLevel),
  };
}

function inferCategory(key: BusinessDomain): string {
  if (key.startsWith('internal_') || key.endsWith('_entity')) return 'entity';
  if (key.endsWith('_item')) return 'item';
  if (
    key.startsWith('finance_') ||
    ['legal_entity', 'currency_rate', 'intercompany'].includes(key)
  ) {
    return 'finance';
  }
  if (key.endsWith('_space')) return 'space';
  if (key.startsWith('knowledge_')) return 'knowledge';
  if (key.endsWith('_formation') || key.endsWith('_performance')) {
    return 'appeal';
  }
  if (key.startsWith('calendar_')) return 'calendar';
  if (key.startsWith('conscious_')) return 'conscious';
  if (key.startsWith('flow_') || key.startsWith('stream_') || key === 'action_binding') {
    return 'event';
  }
  if (key === 'warehouse_picking_list') return 'manufacturing';
  if (key === 'inventory_shortage') return 'item';
  if (
    key.startsWith('manufacturing_') ||
    key === 'production_order' ||
    key === 'quality_inspection'
  ) {
    return 'manufacturing';
  }
  return 'other';
}

function labelFor(key: BusinessDomain): string {
  const display = getBusinessDomainDisplayName(key);
  const entityAuthOverrides: Partial<Record<BusinessDomain, string>> = {
    internal_entity: '내부 구성원',
    external_entity: '외부 조직',
    sellable_item: '판매 가능 항목',
    finance_state: '재무 상태',
    virtual_space: '가상 공간',
    knowledge_report: '보고서',
  };
  return entityAuthOverrides[key] ?? display;
}

/** Full tenant business subdomain permission registry */
export const DOMAIN_PERMISSION_REGISTRY: readonly DomainPermissionEntry[] = [
  // Entity
  entry('internal_entity', { visibleInMvp: true }),
  entry('external_entity', { visibleInMvp: true }),
  entry('user_entity'),
  // Item
  entry('sellable_item', { visibleInMvp: true }),
  entry('service_item', { visibleInMvp: true }),
  entry('asset_item', { visibleInMvp: true }),
  entry('consumable_item', { visibleInMvp: true }),
  entry('raw_material_item', { visibleInMvp: true }),
  // Finance
  entry('finance_state', {
    visibleInMvp: true,
    sensitive: true,
    requiresMfaForApprove: true,
    requiresDbRevalidation: true,
    riskLevel: 'high',
  }),
  entry('finance_flow', { visibleInMvp: true }),
  entry('finance_budget', { visibleInMvp: true }),
  entry('legal_entity', {
    sensitive: true,
    requiresMfaForApprove: true,
    requiresDbRevalidation: true,
    riskLevel: 'high',
  }),
  entry('currency_rate', {
    sensitive: true,
    requiresDbRevalidation: true,
    riskLevel: 'medium',
  }),
  entry('intercompany', {
    sensitive: true,
    requiresMfaForApprove: true,
    requiresDbRevalidation: true,
    riskLevel: 'high',
  }),
  // Space
  entry('physical_space', { visibleInMvp: true }),
  entry('virtual_space', { visibleInMvp: true }),
  // Knowledge
  entry('knowledge_report', { visibleInMvp: true }),
  entry('knowledge_policy'),
  entry('knowledge_plan'),
  // Appeal
  entry('marketing_campaign' as BusinessDomain),
  entry('content_asset' as BusinessDomain),
  entry('semantic_formation'),
  entry('ephemeral_formation'),
  entry('recurrent_performance'),
  entry('ephemeral_performance'),
  // Calendar
  entry('calendar_anchor'),
  entry('calendar_trigger', { visibleInMvp: true }),
  entry('calendar_cycle'),
  // Event / flow (BP-RC1 Phase 1 registry closure)
  entry('flow_definition'),
  entry('stream_definition'),
  entry('action_binding'),
  entry('flow_execution'),
  entry('stream_execution', { visibleInMvp: true }),
  entry('flow_event'),
  entry('stream_event'),
  // Warehouse / inventory hubs (WB4.3-S1 · shared infra)
  entry('warehouse_picking_list' as BusinessDomain),
  entry('inventory_shortage' as BusinessDomain),
  // Manufacturing
  entry('manufacturing_bom', { visibleInMvp: true }),
  entry('production_order', { visibleInMvp: true }),
  entry('quality_inspection', { visibleInMvp: true }),
  // Conscious hub (BP-RC1 route config · workbench adapter registry)
  entry('conscious_alert' as BusinessDomain, { category: 'conscious' }),
  entry('conscious_suggestion' as BusinessDomain, { category: 'conscious' }),
  entry('conscious_memory' as BusinessDomain, { category: 'conscious' }),
  entry('conscious_wisdom' as BusinessDomain, { category: 'conscious' }),
  entry('conscious_brain' as BusinessDomain, { category: 'conscious' }),
  entry('conscious_stream' as BusinessDomain, { category: 'conscious' }),
  entry('conscious_learning' as BusinessDomain, { category: 'conscious' }),
  entry('conscious_pattern' as BusinessDomain, { category: 'conscious' }),
  entry('conscious_neuro' as BusinessDomain, { category: 'conscious' }),
  entry('conscious_org_state' as BusinessDomain, { category: 'conscious' }),
] as const;

const REGISTRY_BY_KEY = new Map(DOMAIN_PERMISSION_REGISTRY.map((e) => [e.key, e] as const));

export function getPermissionEntry(key: string): DomainPermissionEntry | undefined {
  return REGISTRY_BY_KEY.get(key as BusinessDomain);
}

export function isSensitiveSubdomain(key: string): boolean {
  return getPermissionEntry(key)?.sensitive === true;
}

export function requiresMfaForSubdomainApprove(key: string): boolean {
  return getPermissionEntry(key)?.requiresMfaForApprove === true;
}

export function requiresDbRevalidation(key: string): boolean {
  return getPermissionEntry(key)?.requiresDbRevalidation === true;
}

export function getRiskLevel(key: string): RiskLevel {
  return getPermissionEntry(key)?.riskLevel ?? 'low';
}

/** Global permissions that always require sensitive-action handling (not subdomain-scoped). */
export const GLOBAL_SENSITIVE_PERMISSIONS = [
  'document:approve',
  'permission:delegate',
  'flow:approve',
] as const;

export type GlobalSensitivePermission = (typeof GLOBAL_SENSITIVE_PERMISSIONS)[number];

const FLOW_APPROVE_SCOPED_RE = /^flow:approve:.+$/;

/** MVP delegatable subdomains for KD-3 permission matrix */
export function getMvpDelegatableSubdomains(): readonly MvpDelegatableSubdomain[] {
  return MVP_DELEGATABLE_KEYS;
}

const APPROVE_PERMISSION_RE = /^([a-z_]+):approve$/;

/**
 * CR3 SSOT — true for approve-family permissions requiring DB revalidation or step-up.
 * Covers global bridge keys, scoped flow:approve:{subdomain}, and sensitive subdomain approve.
 */
export function isSensitivePermission(permission: string): boolean {
  if ((GLOBAL_SENSITIVE_PERMISSIONS as readonly string[]).includes(permission)) {
    return true;
  }
  if (FLOW_APPROVE_SCOPED_RE.test(permission)) {
    return true;
  }
  const match = permission.match(APPROVE_PERMISSION_RE);
  if (!match?.[1]) return false;
  return requiresDbRevalidation(match[1]) || isSensitiveSubdomain(match[1]);
}

/**
 * Stream B helper — alias for isSensitivePermission (registry SSOT).
 */
export function isSensitiveAction(permission: string): boolean {
  return isSensitivePermission(permission);
}

/** Workspace UX §8 — 4-family goal permission keys (backward-compat bridge to 현행 strings) */
export type GoalPermissionFamily = 'object' | 'operation' | 'flow' | 'insight';

export interface GoalPermissionEntry {
  key: string;
  family: GoalPermissionFamily;
  label: string;
  /** 현행 구현 permission — goal key 없을 때 bridge (optional) */
  bridgePermission?: string;
  /** KD-3 matrix UI에서 위임 가능 */
  delegatable: boolean;
}

/** Global operation bridge keys — 현행 impl (backward compat) */
export const FLOW_EXECUTE_GLOBAL = 'flow:execute' as const;
export const FLOW_APPROVE_GLOBAL = 'flow:approve' as const;

/** Flow catalog umbrella goal key — WORKSPACE_UX §5 · §8 */
export const FLOW_START_GLOBAL = 'flow:start' as const;

/** Insights umbrella goal key — WORKSPACE_UX §6 */
export const INSIGHT_VIEW_GLOBAL = 'insight:view' as const;

/** Scoped insight view pattern — insight:{domain}:view */
export const INSIGHT_SCOPED_VIEW_RE = /^insight:.+:view$/;

/** Builds scoped flow execute permission — flow:execute:{subdomain} */
export function buildScopedFlowExecutePermission(subdomain: string): string {
  return `${FLOW_EXECUTE_GLOBAL}:${subdomain}`;
}

/** Builds scoped flow approve permission — flow:approve:{subdomain} */
export function buildScopedFlowApprovePermission(subdomain: string): string {
  return `${FLOW_APPROVE_GLOBAL}:${subdomain}`;
}

/** Template goal key — flow:{flowId}:start (WORKSPACE_UX §5.2) */
export function buildFlowGoalStartPermission(flowId: string): string {
  return `flow:${flowId}:start`;
}

/** Voucher template goal key — voucher.{streamType}:start (WORKSPACE_UX §5.2) */
export function buildVoucherGoalStartPermission(voucherStreamType: string): string {
  return `voucher.${voucherStreamType}:start`;
}

/** True when permission is a scoped insight view key (not the umbrella insight:view). */
export function isScopedInsightViewPermission(permission: string): boolean {
  return INSIGHT_SCOPED_VIEW_RE.test(permission);
}

/** W-W3 Insights gate — umbrella or any insight:*:view scoped key. */
export function hasInsightViewPermission(permissions: readonly string[]): boolean {
  if (permissions.includes(INSIGHT_VIEW_GLOBAL)) {
    return true;
  }
  return permissions.some(isScopedInsightViewPermission);
}

/** Umbrella + scoped insight keys — WORKSPACE_UX §6.2 */
export const INSIGHT_GOAL_PERMISSION_KEYS = [
  INSIGHT_VIEW_GLOBAL,
  'insight:finance_risk:view',
  'insight:employee_burnout:view',
  'insight:supplier_risk:view',
  'insight:execution_quality:view',
  'insight:customer_satisfaction:view',
] as const;

export type InsightGoalPermissionKey = (typeof INSIGHT_GOAL_PERMISSION_KEYS)[number];

/** Operation bridge keys registered in goal catalog */
export const OPERATION_BRIDGE_PERMISSION_KEYS = [FLOW_EXECUTE_GLOBAL, FLOW_APPROVE_GLOBAL] as const;

export type OperationBridgePermissionKey = (typeof OPERATION_BRIDGE_PERMISSION_KEYS)[number];

/** Scoped flow execute keys for MVP subdomains — WORKSPACE_UX §8 Operation family */
export const SCOPED_FLOW_EXECUTE_PERMISSION_KEYS = MVP_DELEGATABLE_KEYS.map((subdomain) =>
  buildScopedFlowExecutePermission(subdomain)
) as readonly string[];

/** Flow catalog goal keys — WORKSPACE_UX §8 */
export const FLOW_GOAL_PERMISSION_KEYS = [FLOW_START_GLOBAL] as const;

export type FlowGoalPermissionKey = (typeof FLOW_GOAL_PERMISSION_KEYS)[number];

const INSIGHT_GOAL_LABELS: Record<InsightGoalPermissionKey, string> = {
  [INSIGHT_VIEW_GLOBAL]: 'Insights 전체 (insight:view)',
  'insight:finance_risk:view': '재무 리스크 판단',
  'insight:employee_burnout:view': '구성원 소진 판단',
  'insight:supplier_risk:view': '공급자 리스크 판단',
  'insight:execution_quality:view': '실행 품질 판단',
  'insight:customer_satisfaction:view': '고객 만족 판단',
};

const OPERATION_BRIDGE_LABELS: Record<OperationBridgePermissionKey, string> = {
  [FLOW_EXECUTE_GLOBAL]: 'Flow 실행 (전역 · flow:execute)',
  [FLOW_APPROVE_GLOBAL]: 'Flow 결재 (전역 · flow:approve)',
};

function insightGoalEntry(key: InsightGoalPermissionKey): GoalPermissionEntry {
  return {
    key,
    family: 'insight',
    label: INSIGHT_GOAL_LABELS[key],
    delegatable: true,
  };
}

function operationBridgeEntry(key: OperationBridgePermissionKey): GoalPermissionEntry {
  return {
    key,
    family: 'operation',
    label: OPERATION_BRIDGE_LABELS[key],
    delegatable: true,
  };
}

function scopedFlowExecuteEntry(subdomain: MvpDelegatableSubdomain): GoalPermissionEntry {
  const subdomainLabel = labelFor(subdomain);
  return {
    key: buildScopedFlowExecutePermission(subdomain),
    family: 'operation',
    label: `Flow 실행 (${subdomainLabel})`,
    delegatable: true,
  };
}

/** Goal permission registry — §8 목표 키 catalog (현행 subdomain permissions 별도) */
export const GOAL_PERMISSION_REGISTRY: readonly GoalPermissionEntry[] = [
  ...INSIGHT_GOAL_PERMISSION_KEYS.map(insightGoalEntry),
  ...OPERATION_BRIDGE_PERMISSION_KEYS.map(operationBridgeEntry),
  ...MVP_DELEGATABLE_KEYS.map(scopedFlowExecuteEntry),
  {
    key: FLOW_START_GLOBAL,
    family: 'flow',
    label: 'Flow 시작 (flow:start)',
    bridgePermission: FLOW_EXECUTE_GLOBAL,
    delegatable: true,
  },
] as const;

const GOAL_PERMISSION_BY_KEY = new Map(GOAL_PERMISSION_REGISTRY.map((e) => [e.key, e] as const));

export function getGoalPermissionEntry(key: string): GoalPermissionEntry | undefined {
  return GOAL_PERMISSION_BY_KEY.get(key);
}

export function isGoalPermission(key: string): boolean {
  return GOAL_PERMISSION_BY_KEY.has(key);
}

/** KD-3 matrix — delegatable goal keys only */
export function getDelegatableGoalPermissionKeys(): readonly string[] {
  return GOAL_PERMISSION_REGISTRY.filter((e) => e.delegatable).map((e) => e.key);
}
